import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import EventCard from './EventCard.jsx';
import useFetch from './useFetch.jsx';
import { domain } from './Domain.jsx';
import { 
    Search, Filter, Calendar, MapPin, Users, Star, 
    Eye, Heart, ArrowRight, Clock, Globe, Tag,
    ChevronLeft, ChevronRight, Grid, List, Share2, History
} from 'lucide-react';

const PassedEvents = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedAgeRestriction, setSelectedAgeRestriction] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [currentPage, setCurrentPage] = useState(1);
    const [eventsPerPage] = useState(12);
    const [categories, setCategories] = useState([]);

    const navigate = useNavigate();

    // Fetch events and categories
    const { data: eventsData, loading: eventsLoading } = useFetch(`${domain}/event-details?passed_events=true`);
    const { data: categoriesData } = useFetch(`${domain}/event-categories`);

    useEffect(() => {
        if (eventsData) {
            // Backend already filters to show only passed events within the last 4 months
            setEvents(eventsData);
            setLoading(false);
        }
    }, [eventsData]);

    useEffect(() => {
        if (categoriesData) {
            setCategories(categoriesData);
        }
    }, [categoriesData]);

    // Filter events based on search and filters
    const filteredEvents = events.filter(event => {
        const matchesSearch = !searchTerm || 
            event.event_plot_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.event_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.venue_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.organiser?.organiser_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.event_category?.name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesCategory = selectedCategory === 'all' || 
            event.event_category?.slug === selectedCategory;

        const matchesAgeRestriction = !selectedAgeRestriction || 
            event.age_restriction === selectedAgeRestriction;

        return matchesSearch && matchesCategory && matchesAgeRestriction;
    });

    // Pagination
    const indexOfLastEvent = currentPage * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
    const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    // Truncate text
    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Age restriction options
    const ageRestrictions = [
        { value: '', label: 'All Ages' },
        { value: '18+', label: '18+' },
        { value: '21+', label: '21+' },
        { value: '13+', label: '13+' },
        { value: 'Family Friendly', label: 'Family Friendly' },
        { value: '5-12', label: '5-12 Years' }
    ];

    const resetFilters = () => {
        setSearchTerm('');
        setSelectedCategory('all');
        setSelectedAgeRestriction('');
        setCurrentPage(1);
    };

    const EventListItem = ({ event }) => (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
        >
            <div className="flex flex-col lg:flex-row gap-6">
                <div className="lg:w-1/3">
                    <img 
                        src={domain + event.banner} 
                        alt={event.event_plot_name}
                        className="w-full h-48 object-cover rounded-lg"
                        onError={(e) => {
                            e.target.src = '/images/default-event.jpg';
                        }}
                    />
                </div>
                
                <div className="lg:w-2/3 space-y-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                    {event.event_category?.name}
                                </span>
                                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                                    {event.age_restriction}
                                </span>
                                <span className="bg-gray-400 text-white px-3 py-1 rounded-full text-sm">
                                    Past Event
                                </span>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                {event.event_plot_name}
                            </h3>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                <span>{event.views || 0}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Heart className="w-4 h-4 text-red-500" />
                                <span>{event.likes || 0}</span>
                            </div>
                        </div>
                    </div>
                    
                    <p className="text-gray-600 leading-relaxed">
                        {truncateText(event.event_description, 200)}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-orange-500" />
                            <span>Ended: {formatDate(event.end_date_event)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-teal-500" />
                            <span>{event.venue_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-500" />
                            <span>By {event.organiser?.organiser_name}</span>
                        </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <History className="w-4 h-4" />
                            <span>Event ended {formatDate(event.end_date_event)}</span>
                        </div>
                        
                        <Link 
                            to={`/events/${event.slug}`}
                            className="flex items-center gap-2 bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200"
                        >
                            View Details
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </motion.div>
    );

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-12 md:py-16">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="flex items-center justify-center gap-3 mb-4">
                                <History className="w-8 h-8 md:w-10 md:h-10" />
                                <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold">
                                    Passed Events
                                </h1>
                            </div>
                            <p className="text-sm md:text-xl text-orange-100 mb-6 md:mb-8">
                                Browse all past events
                            </p>
                            
                            {/* Search Bar */}
                            <div className="relative max-w-2xl mx-auto">
                                <Search className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search passed events..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 md:pl-12 pr-3 md:pr-4 py-3 md:py-4 bg-white text-gray-800 rounded-xl shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm md:text-base"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Results */}
                <div className="container mx-auto px-4 py-8">
                    {/* Filters */}
                    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6 md:mb-8">
                        <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
                            {/* Category Filter */}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category
                                </label>
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="w-full px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                                >
                                    <option value="all">All Categories</option>
                                    {categories.map(category => (
                                        <option key={category.id} value={category.slug}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Age Restriction Filter */}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Age Restriction
                                </label>
                                <select
                                    value={selectedAgeRestriction}
                                    onChange={(e) => setSelectedAgeRestriction(e.target.value)}
                                    className="w-full px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                                >
                                    {ageRestrictions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* View Mode Toggle */}
                            <div className="flex items-end">
                                <div className="flex bg-gray-100 rounded-lg p-1">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-1.5 md:p-2 rounded-md transition-colors duration-200 ${
                                            viewMode === 'grid' 
                                                ? 'bg-white text-orange-500 shadow-sm' 
                                                : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                    >
                                        <Grid className="w-4 h-4 md:w-5 md:h-5" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`p-1.5 md:p-2 rounded-md transition-colors duration-200 ${
                                            viewMode === 'list' 
                                                ? 'bg-white text-orange-500 shadow-sm' 
                                                : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                    >
                                        <List className="w-4 h-4 md:w-5 md:h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Reset Filters */}
                            <div className="flex items-end">
                                <button
                                    onClick={resetFilters}
                                    className="px-3 py-2 md:px-4 md:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-sm"
                                >
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Results Summary */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 md:mb-6">
                        <div className="text-sm md:text-base text-gray-600">
                            Showing {filteredEvents.length} of {events.length} passed events
                        </div>
                        <div className="text-sm md:text-base text-gray-600">
                            Page {currentPage} of {totalPages}
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="text-center py-8 md:py-12">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-orange-500"></div>
                            <p className="mt-2 text-sm md:text-base text-gray-600">Loading passed events...</p>
                        </div>
                    )}

                    {/* Events Grid/List */}
                    {!loading && (
                        <>
                            {currentEvents.length > 0 ? (
                                <div className={viewMode === 'grid' 
                                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6' 
                                    : 'space-y-6'
                                }>
                                    <AnimatePresence>
                                        {currentEvents.map((event, index) => (
                                            <div key={event.id || index}>
                                                {viewMode === 'grid' ? (
                                                    <EventCard event={event} />
                                                ) : (
                                                    <EventListItem event={event} />
                                                )}
                                            </div>
                                        ))}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <div className="text-center py-8 md:py-12">
                                    <div className="w-16 h-16 md:w-24 md:h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                                        <History className="w-8 h-8 md:w-12 md:h-12 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg md:text-2xl font-bold text-gray-800 mb-2">
                                        No Passed Events Found
                                    </h3>
                                    <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                                        {events.length === 0 
                                            ? 'No passed events found'
                                            : 'Try adjusting your search criteria or filters'
                                        }
                                    </p>
                                    {events.length > 0 && (
                                        <button
                                            onClick={resetFilters}
                                            className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg font-medium transition-all duration-200 text-sm md:text-base"
                                        >
                                            Clear All Filters
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-1 md:gap-2 mt-6 md:mt-8">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-sm"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        <span className="hidden sm:inline">Previous</span>
                                    </button>
                                    
                                    <div className="flex gap-1">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`px-2 py-2 md:px-3 md:py-2 rounded-lg transition-colors duration-200 text-sm ${
                                                    currentPage === page
                                                        ? 'bg-orange-500 text-white'
                                                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-sm"
                                    >
                                        <span className="hidden sm:inline">Next</span>
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};

export default PassedEvents;

